<script setup lang="ts">
import {cx} from 'class-variance-authority'

import {useNav} from './nav'
const NuxtLink = resolveComponent('NuxtLink')
const localePath = useLocalePath()
const nav = useNav()


const groupClass = cx('rounded border border-gray-200 bg-gray-50 py-2')
const linkClass = cx('flex w-full items-center space-x-2 px-4 py-2 text-sm font-medium transition hover:bg-gray-200')
const groupHeadingClass = cx('px-4 py-2 text-sm !text-gray-500')


const isOpen = ref(false)
function onClose() {
  isOpen.value = false
}
function onOpen() {
  isOpen.value = !isOpen.value
}


onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', e => {
      onClose()
    })
  }
})
</script>

<template>
  <div class="flex h-full items-center">
    <PopoverRoot :open="isOpen">
      <PopoverTrigger
        @click="onOpen"
        class="relative inline-flex h-8 w-10 items-center justify-center rounded-full text-gray-600 bordershadow-input-light"
        :aria-label="$t('header.nav.toggle.open')"
      >
        <Icon name="lucide:menu" size="18px" />
      </PopoverTrigger>

      <ClientOnly>
        <PopoverPortal to="#nav-popover">
          <PopoverContent
            @interact-outside="onClose"
            as="section"
            side="bottom"
            :side-offset="-1"
            :class="[
              'absolute -right-6 -top-8 z-[9999] w-72 rounded-lg bg-white p-4 bordershadow-lg-gray',
              'origin-top-right will-change-[transform,opacity] data-[state=closed]:data-[side=bottom]:animate-scaleOut data-[state=open]:data-[side=bottom]:animate-scaleIn',
              'flex max-h-[80vh] flex-col space-y-2 overflow-hidden',
            ]"
          >
            <header class="flex items-center justify-between">
              <Heading as="h2" size="small"> Menu </Heading>
              <PopoverClose
                class="inline-flex h-8 w-10 items-center justify-center rounded-full text-gray-600 bordershadow-input-light"
                :aria-label="$t('header.nav.toggle.close')"
              >
                <Icon name="lucide:x" size="18px" />
              </PopoverClose>
            </header>
            <main class="flex flex-col space-y-2 overflow-y-auto">
              <template v-for="link of nav" :key="link.label">
                <NuxtLink
                  v-if="link.type === 'internal'"
                  :to="link.to"
                  :class="cx(linkClass, 'rounded')"
                  @click="onClose"
                >
                  <Paragraph size="small">
                    {{ link.label }}
                  </Paragraph>
                </NuxtLink>

                <div v-else-if="link.type === 'subnav' || link.type === 'subnav-work'" :class="groupClass">
                  <Heading as="p" size="small" :class="groupHeadingClass">
                    {{ link.label }}
                  </Heading>

                  <div class="flex flex-col">
                    <NuxtLink
                      v-if="link.highlight?.type === 'internal'"
                      :to="link.highlight.to"
                      :class="linkClass"
                      @click="onClose"
                    >
                      <Paragraph size="small">
                        {{ link.highlight.label }}
                      </Paragraph>
                    </NuxtLink>

                    <NuxtLink
                      v-for="l of link.links"
                      :key="l.label"
                      :to="l.type === 'internal' ? l.to : ''"
                      :class="linkClass"
                      @click="onClose"
                    >
                      <Paragraph size="small">
                        {{ l.label }}
                      </Paragraph>
                    </NuxtLink>
                  </div>
                </div>
              </template>
            </main>

            <footer>
              <Action intent="main" size="sm" as="link" :to="localePath('/contact')" @click="onClose">
                {{ $t('header.nav.contact') }}
              </Action>
            </footer>
          </PopoverContent>
        </PopoverPortal>
      </ClientOnly>
    </PopoverRoot>
  </div>
</template>
